import type { RefObject } from 'react'
import { BG, SHADE_SIDE, type TurnGeometry } from './geometry'
import { MARK_FILL, MARK_H, markPath, type RunMarks } from './marks'

/* =========================================
   CONVEYOR BELT (page-fixed pieces)
   A thin, gently undulating paper ribbon — no slats, just the wavy
   surfaces themselves. The ripple TRAVELS along the belt: the
   component's rAF loop (see index.tsx) rewrites the four wavy paths'
   `d` attributes through the refs passed in here.

   Both components receive the turn-dependent geometry (built at
   runtime so the fold sits at a fixed distance from the screen's
   left edge — see geometry.ts).
   ========================================= */

export interface WaveRefs {
  inTop: RefObject<SVGPathElement | null>
  inSide: RefObject<SVGPathElement | null>
  outTop: RefObject<SVGPathElement | null>
  outSide: RefObject<SVGPathElement | null>
  marks: RefObject<SVGGElement | null>
}

/* Belt ribbon behind the block. Drawn FIRST: the block and the
   outgoing surfaces are painted over it, so only the stretch behind
   the block shows through. Top surface faces the light; the side
   falls into shade. */
export function BeltStrip({
  turn,
  waveRefs,
  runMarks,
}: {
  turn: TurnGeometry
  waveRefs: WaveRefs
  runMarks: RunMarks
}) {
  return (
    <>
      <path
        ref={waveRefs.inTop}
        d={turn.inTop}
        fill={BG}
        vectorEffect="non-scaling-stroke"
      />
      <path
        ref={waveRefs.inSide}
        d={turn.inSide}
        fill={SHADE_SIDE}
        vectorEffect="non-scaling-stroke"
      />
      {/* Faux print on the incoming paper — pill shapes of varied
          length, each drawn as a thick round-capped stroke of a path
          that follows the wavy surface, so the pills BEND with the
          undulation. Rebuilt each frame. Drawn only here (behind the
          block): the block and the outgoing surfaces paint over them,
          so the marks ride in from the top right and vanish into the
          block. */}
      <g
        ref={waveRefs.marks}
        fill="none"
        stroke={MARK_FILL}
        strokeWidth={MARK_H}
        strokeLinecap="round"
      >
        {runMarks.marks.map((m, i) => (
          <path key={i} d={markPath(m, turn.turnZ, runMarks.laneLen, 0, 0, 0)} />
        ))}
      </g>
    </>
  )
}

/* The run past the block, drawn LAST. The group sits inside the
   MOVING slot clip (#belt-out-clip) so it is cut off exactly at the
   block's face wherever the block currently is; the run continues
   off the viewport's left edge. */
export function OutgoingBelt({
  turn,
  waveRefs,
}: {
  turn: TurnGeometry
  waveRefs: WaveRefs
}) {
  return (
    <g clipPath="url(#belt-out-clip)">
      <path
        ref={waveRefs.outTop}
        d={turn.outTop}
        fill={BG}
        vectorEffect="non-scaling-stroke"
      />
      <path
        ref={waveRefs.outSide}
        d={turn.outSide}
        fill={SHADE_SIDE}
        vectorEffect="non-scaling-stroke"
      />
    </g>
  )
}
